"use client";

import {
   ReactNode,
   createContext,
   useCallback,
   useContext,
   useEffect,
   useMemo,
   useRef,
   useState,
} from "react";
import {useAuthContext} from "@/contexts/common/AuthContextProvider";
import {useOrg} from "@/hooks/useOrg";
import {EodReportResponse} from "@/types/reporting";
import {apiFetch} from "@/lib/api";
import {
   buildEodReportAcknowledgement,
   findUserAcknowledgement,
   getUserAcknowledgements,
   setUserAcknowledgement,
} from "@/utils/acknowledgements/userAcknowledgements";
import {canAccessEodReport} from "@/utils/reporting/access";

/** localStorage key for tracking the last seen EOD report */
export const EOD_REPORT_LAST_SEEN_KEY = "eod_report_last_seen_id";


type EodReportAlertContextType = {
   /** Whether there is a new report available that hasn't been seen */
   hasAlert: boolean;
   /** Whether the hook is currently loading */
   isLoading: boolean;
   /** Mark the current report as seen */
   markAsSeen: () => void;
   /** The current report ID if available */
   currentReportId: string | null;
};

const EodReportAlertContext = createContext<EodReportAlertContextType | null>(null);

async function resolveServerSeenReportState(
   userId: string | undefined,
   reportId: string,
   isLocallySeen: boolean
): Promise<boolean> {
   if (!userId) return false;

   const descriptor = buildEodReportAcknowledgement(reportId);
   try {
      const acknowledgements = await getUserAcknowledgements(userId);
      const isServerSeen = Boolean(findUserAcknowledgement(acknowledgements, descriptor));

      if (isLocallySeen && !isServerSeen) {
         setUserAcknowledgement(userId, descriptor, "seen", {
            source: "legacy_eod_report_alert",
         }).catch(() => undefined);
      } else if (isServerSeen && !isLocallySeen) {
         localStorage.setItem(EOD_REPORT_LAST_SEEN_KEY, reportId);
      }

      return isServerSeen;
   } catch {
      return false;
   }
}

function persistSeenReport(userId: string | undefined, reportId: string, source: string) {
   localStorage.setItem(EOD_REPORT_LAST_SEEN_KEY, reportId);
   if (!userId) return;

   setUserAcknowledgement(userId, buildEodReportAcknowledgement(reportId), "seen", {
      source,
   }).catch(() => undefined);
}

/**
 * Provider component that manages EOD report alert state.
 * Should be placed high in the component tree so all consumers share the same state.
 */
export function EodReportAlertProvider({children}: Readonly<{children: ReactNode}>) {
   const {isAuthenticated, user} = useAuthContext();
   const {productAccess} = useOrg();
   const hasEodAccess = canAccessEodReport(productAccess.gailAgentEnabled);
   const [hasAlert, setHasAlert] = useState(false);
   const [isLoading, setIsLoading] = useState(true);
   const [currentReportId, setCurrentReportId] = useState<string | null>(null);
   const isMountedRef = useRef(true);

   const checkForAlert = useCallback(async () => {
      if (!isAuthenticated || !hasEodAccess || productAccess.loading) {
         setIsLoading(false);
         return;
      }

      try {
         const response = await apiFetch<EodReportResponse>("/api/reporting/eod-report", "GET");

         // Check if component is still mounted before updating state
         if (!isMountedRef.current) return;

         if (response && response.available && response.reportId) {
            setCurrentReportId(response.reportId);

            // Check if this report has been seen
            const lastSeenId = localStorage.getItem(EOD_REPORT_LAST_SEEN_KEY);
            const isLocallySeen = lastSeenId === response.reportId;
            const isServerSeen = await resolveServerSeenReportState(
               user?.id,
               response.reportId,
               isLocallySeen
            );

            setHasAlert(!isLocallySeen && !isServerSeen);
         } else {
            setCurrentReportId(null);
            setHasAlert(false);
         }
      } catch (error) {
         console.error("Failed to check EOD report alert:", error);
         if (isMountedRef.current) {
            setHasAlert(false);
         }
      } finally {
         if (isMountedRef.current) {
            setIsLoading(false);
         }
      }
   }, [isAuthenticated, hasEodAccess, productAccess.loading, user?.id]);

   const markAsSeen = useCallback(() => {
      if (currentReportId) {
         persistSeenReport(user?.id, currentReportId, "eod_report_alert");
         setHasAlert(false);
      }
   }, [currentReportId, user?.id]);

   // Check once on mount only — no global polling or focus listener.
   // The full report is fetched on the reporting page when the user visits it.
   useEffect(() => {
      isMountedRef.current = true;
      checkForAlert();

      return () => {
         isMountedRef.current = false;
      };
   }, [checkForAlert]);

   const contextValue = useMemo(
      () => ({
         hasAlert,
         isLoading,
         markAsSeen,
         currentReportId,
      }),
      [hasAlert, isLoading, markAsSeen, currentReportId]
   );

   return (
      <EodReportAlertContext.Provider value={contextValue}>
         {children}
      </EodReportAlertContext.Provider>
   );
}

/**
 * Hook to access EOD report alert state.
 * Must be used within an EodReportAlertProvider.
 *
 * All components using this hook share the same state - only one API call is made.
 */
export function useEodReportAlert(): EodReportAlertContextType {
   const context = useContext(EodReportAlertContext);

   // If no provider, return safe defaults (for backwards compatibility and SSR)
   if (!context) {
      return {
         hasAlert: false,
         isLoading: false,
         markAsSeen: () => {},
         currentReportId: null,
      };
   }

   return context;
}
